import {IntegrationAlert} from "./integration";

export enum TicketStatus {
  NEW = "new",
  ASSIGNED = "assigned",
  IN_PROGRESS = "in_progress",
  PENDING = "pending",
  RESOLVED = "resolved",
  ESCALATED = "escalated",
  CLOSED = "closed",
  FALSE_POSITIVE = "false_positive",
}

// Ticket priority enum
export enum TicketPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical"
}

// Ticket category enum
export enum TicketCategory {
  MALWARE = "malware",
  PHISHING = "phishing",
  UNAUTHORIZED_ACCESS = "unauthorized_access",
  DATA_BREACH = "data_breach",
  DENIAL_OF_SERVICE = "denial_of_service",
  INSIDER_THREAT = "insider_threat",
  POLICY_VIOLATION = "policy_violation",
  SUSPICIOUS_ACTIVITY = "suspicious_activity",
  OTHER = "other"
}

export enum Role {
  ADMIN = "global_admin",
  OPERATTOR = "operator",
  CLIENT = "client_user",
}

// User interface
export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  is_active: boolean;
  soc_id?: string;
}

export interface UsersState {
  users: User[];
  selectedUser: User | null;
  userTickets: Ticket[]; 
  isLoading: boolean;
  error: string | null;
}

// Ticket interface - matches Ticket.to_dict() from backend
export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  client_id: string;
  client_name: string | null;
  integration_alert_ids: string[];
  integration_alerts?: IntegrationAlert[];
  created_by_id: string | null;
  assignee_id: string | null;
  created_by: User | null;
  assignee: User | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  due_date: string | null;
  comments_count: number;
  attachments_count: number;
  raw_data: any | null;
  // Analyst-authored fields surfaced to the client
  client_recommendations: string | null;
  resolution_summary: string | null;
  // 'auto' = created by the policy engine, 'manual' = created by an analyst
  source?: 'auto' | 'manual';
  requires_human_review?: boolean;
  hitl_reason?: string | null;
  quality_signal?: 'strong' | 'limited' | 'pending' | null;
}

// Ticket activity / audit trail
export type TicketActivityType =
  | 'ticket_created'
  | 'status_changed'
  | 'assigned'
  | 'unassigned'
  | 'resolved'
  | 'escalated'
  | 'priority_changed';

export interface TicketActivity {
  id: string;
  type: TicketActivityType;
  actor: User | null;
  from_value: string | null;
  to_value: string | null;
  description: string | null;
  created_at: string;
}

// Ticket comment interface
export interface TicketComment {
  id: string;
  ticket_id: string;
  author: User | null;
  content: string;
  is_internal: boolean;
  created_at: string;
  updated_at: string;
}

// Ticket attachment interface
export interface TicketAttachment {
  id: string;
  ticket_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  uploaded_by: User | null;
  created_at: string;
}

// Authentication state interface
export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Tickets state interface
export interface TicketsState {
  tickets: Ticket[];
  selectedTicket: Ticket | null;
  comments: TicketComment[];
  attachments: TicketAttachment[];
  activity: TicketActivity[];
  isLoading: boolean;
  isDetailsLoading: boolean;
  error: string | null;
}

// Client interface
export interface Client {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  tickets_count: number;
  crowdstrike_client_id: string | null;
  crowdstrike_client_secret: string | null;
}

// Client policy (auto-ticket thresholds + custom rule overrides)
export interface ClientPolicy {
  id?: string;
  client_id: string;
  auto_ticket_severities: string[];
  custom_rules: any[];
  created_at?: string;
  updated_at?: string;
}

// Client asset (imported during onboarding)
export interface ClientAsset {
  id: string;
  client_id: string;
  name: string | null;
  hostname: string | null;
  ip_address: string | null;
  asset_type: string;
  criticality: 'critical' | 'high' | 'medium' | 'low';
  department: string | null;
  owner: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
}

// Client access interface
export interface ClientAccess {
  id: string;
  client_id: string;
  user_id: string;
  created_at: string;
}

// Clients state interface
export interface ClientsState {
  clients: Client[];
  selectedClient: Client | null;
  userAccess: ClientAccess[];
  isLoading: boolean;
  isFetchingCrowdstrikeTickets: boolean;
  crowdstrikeTicketsMessage: string | null;
  policy: ClientPolicy | null;
  policyLoading: boolean;
  assets: ClientAsset[];
  assetsLoading: boolean;
  error: string | null;
}

// Decoding agent state interface
export interface DecodingAgentState {
  result: string | null;
  isLoading: boolean;
  error: string | null;
}

// Alert interface - matches backend IntegrationAlert.to_dict()
export interface Alert {
  id: string;
  title: string;
  username: string;
  description: string;
  severity: string;
  risk_score: number;
  status: string;
  hostname: string,
  ip_address: string;
  file_name: string;
  file_hash: string;
  mitre_tactic: string;
  mitre_technique: string;
  action_taken: string;
  client_id: string;
  client_name: string;
  integration_id: string;
  integration_type: string;
  external_id: string;
  detected_at: string | null;
  occurred_at: string | null;
  created_at: string;
  updated_at: string;
  raw_data: any | null;
  ticket_id: string | null;
  in_cluster: boolean;
  correlation_ids: string[];
}


// Create Alert request interface
export interface CreateAlertRequest {
  title: string;
  description: string;
  severity: string;
  status: string;
  client_id: string;
  integration_type?: string;
  raw_data?: any;
}

// Alerts state interface
export interface AlertsState {
  alerts: Alert[];
  selectedAlert: Alert | null;
  isLoading: boolean;
  isDetailsLoading: boolean;
  error: string | null;
}

// Re-export integration types
export * from './integration';

// Re-export ticket details types (extended SOC ticket data)
export * from './ticketDetails';

// Notification types
export type NotificationType =
  | 'ticket_created'
  | 'ticket_status_changed'
  | 'ticket_comment'
  | 'sla_warning'
  | 'sla_breach'
  | 'ai_error_budget_breached';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  ticket_id: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationsState {
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
}

// Correlation types
export interface CorrelationEntity {
  type: string;
  value: string;
  role: string;
  is_derived: boolean;
}

export interface Correlation {
  id: string;
  client_id: string;
  client_name: string | null;
  title: string;
  severity_highest: string | null;
  alert_ids: string[];
  alert_count: number;
  detected_at: string[];
  entities: CorrelationEntity[];
  correlation_score: number;
  status: 'open' | 'dismissed' | 'escalated';
  created_at: string;
  updated_at: string;
}

export interface CorrelationAlertSummary {
  id: string;
  title: string;
  severity: string | null;
  integration_type: string | null;
  hostname: string | null;
  ip_address: string | null;
  username: string | null;
  detected_at: string | null;
  action_taken: string | null;
  ticket_id: string | null;
  mitre_tactic: string | null;
  mitre_technique: string | null;
}

export interface CorrelationDetail extends Correlation {
  alert_summaries: CorrelationAlertSummary[];
}

export interface CorrelationsState {
  correlations: Correlation[];
  selectedCorrelation: CorrelationDetail | null;
  isLoading: boolean;
  isDetailLoading: boolean;
  error: string | null;
  total: number;
}

// IOCEntry interface - matches ManagedIOC.to_dict() from GET /api/iocs
export interface IOCEntry {
  id: string;
  client_id: string;
  ioc_value: string;
  ioc_type: string;
  source: 'alert_enrichment' | 'manual' | 'threat_feed';
  source_ref: string | null;
  reputation_score: number | null;
  is_malicious: boolean | null;
  vendor_data: Record<string, any>;
  // Enrichment convenience fields
  country_code: string | null;
  isp: string | null;
  usage_type: string | null;
  is_tor: boolean;
  vt_detection_count: number | null;
  vt_detection_names: string[];
  // Blocklist
  is_blocked: boolean;
  is_currently_blocked: boolean;
  blocked_at: string | null;
  blocked_until: string | null;
  blocked_by_id: string | null;
  // Metadata
  notes: string | null;
  tags: string[];
  // Timestamps
  last_enriched_at: string | null;
  enrichment_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface HuntMatch {
  alert_id: string;
  alert_title: string;
  severity: string | null;
  detected_at: string | null;
  ticket_id: string | null;
  integration_type: string | null;
}

export interface HuntResponse {
  ioc_id: string;
  ioc_value: string;
  matches: HuntMatch[];
  count: number;
  truncated: boolean;
}

export interface IOCsState {
  iocs: IOCEntry[];
  isLoading: boolean;
  error: string | null;
}

// Playbook types
export interface PlaybookActionFeedback {
  id: string;
  recommendation_id: string;
  session_id: string;
  action: string;
  decision: 'accepted' | 'skipped' | 'added';
  created_at: string;
}

export interface IncidentMemoryMetadata {
  ticket_id: string | null;
  incident_type: string | null;
  ioc_count: number;
  steps_done: number;
  steps_skipped: number;
  hostnames: string[];
  severities: string[];
  outcomes: string[];
}

export interface IncidentMemory {
  id: string;
  client_id: string;
  session_id: string | null;
  incident_type: string | null;
  severity: string | null;
  content_text: string;
  memory_metadata: IncidentMemoryMetadata | null;
  created_at: string;
  updated_at: string | null;
}

export interface RetrievedIncidentRef {
  id: string;
  vector_rank: number;
  bm25_rank: number;
  rrf_score: number;
  reranker_score: number;
}

export interface StepFeedbackEntry {
  id: string;
  session_id: string;
  step_index: number;
  step_text: string;
  decision: 'accepted' | 'skipped';
  created_by_id: string | null;
  created_at: string;
}

export interface PlaybookSession {
  id: string;
  ticket_id: string | null;
  alert_id: string | null;
  client_id: string;
  incident_type: string | null;   // 'ddos' | 'ransomware' | null (IOC-based)
  template_id: string | null;
  ai_summary: string | null;
  ai_next_steps: { step: string; rationale: string }[] | null;
  ai_confidence: number | null;
  retrieved_incident_ids: RetrievedIncidentRef[] | null;
  retrieved_incident_count: number;
  quality_signal: 'strong' | 'limited' | 'pending' | null;
  requires_human_review: boolean;
  hitl_reason: string | null;
  analyst_verdict: 'correct' | 'incorrect' | 'partial' | null;
  analyst_verdict_notes: string | null;
  analyst_verdict_at: string | null;
  analyst_verdict_by_id: string | null;
  analyst_verdict_by_name: string | null;
  step_feedback: StepFeedbackEntry[] | null;
  created_at: string;
}

export type StepStatus = 'pending' | 'done' | 'skipped' | 'na';
export type StepType = 'observe' | 'decide' | 'act';

export interface PlaybookSessionStep {
  id: string;
  session_id: string;
  template_step_id: string | null;
  step_number: number;
  phase: string;
  action: string;
  what_to_check: string | null;
  expected_output: string | null;
  primary_technology: string | null;
  responsible: string | null;
  is_required: boolean;
  step_type: StepType | null;
  status: StepStatus;
  outcome: string | null;
  notes: string | null;
  completed_at: string | null;
  completed_by_id: string | null;
  created_at: string;
}

export interface PlaybookRecommendation {
  id: string;
  session_id: string;
  ioc_value: string | null;
  ioc_type: string | null;
  reputation_score: number | null;
  is_malicious: boolean | null;
  is_tor: boolean | null;
  country_code: string | null;
  isp: string | null;
  usage_type: string | null;
  alert: {
    id: string;
    title: string;
    severity: string;
    hostname: string;
    action_taken: string;
    integration_type: string;
    detected_at: string;
    mitre_tactic: string;
    mitre_technique: string;
  } | null;
  rules_matched: string[];
  confidence: 'high' | 'medium' | 'low' | 'none' | null;
  suggested_actions: string[];
  noise_warnings: string[];
  outcome: 'handled' | 'escalated' | 'false_positive' | null;
  action_feedback: PlaybookActionFeedback[];
  created_at: string;
}

export interface PlaybookStats {
  client_id: string;
  alerts: {
    total: number;
    with_ticket: number;
    auto_ticketed: number;
    manual_ticketed: number;
    without_ticket: number;
  };
  recommendations: {
    total: number;
    by_outcome: Record<string, number>;
    by_confidence: Record<string, number>;
    false_positive_rate_pct: number;
  };
  incidents?: {
    total_sessions: number;
    by_type: Record<string, number>;
  };
}

export interface PlaybookTicketPanel {
  has_playbook: boolean;
  session: PlaybookSession | null;
  steps: PlaybookSessionStep[];
  recommendations: PlaybookRecommendation[];
}

export interface PlaybookTimelineBucket {
  week: string;
  handled: number;
  escalated: number;
  false_positive: number;
  pending: number;
}

export interface PlaybookTimelineStats {
  client_id: string;
  period: string;
  buckets: PlaybookTimelineBucket[];
  false_positive_rate_pct: number;
  trend: 'improving' | 'worsening' | 'stable';
}

export interface PlaybookActionsStats {
  client_id: string;
  total_decisions: number;
  by_decision: Record<string, number>;
  top_accepted: { action: string; count: number }[];
  top_skipped: { action: string; count: number }[];
  top_added: { action: string; count: number }[];
}

export interface PlaybooksState {
  sessions: PlaybookSession[];
  selectedSession: PlaybookSession | null;
  recommendations: PlaybookRecommendation[];
  stats: PlaybookStats | null;
  statsLoading: boolean;
  isLoading: boolean;
  isDetailsLoading: boolean;
  error: string | null;
  pendingHITLTotal: number;
}

// AI Performance / accountability types

export interface AIAccuracyWeeklyBucket {
  week: string;
  accepted: number;
  skipped: number;
  added: number;
  total: number;
  acceptance_rate: number | null;
}

export interface AIAccuracyByIncidentType {
  incident_type: string;
  accepted: number;
  skipped: number;
  added: number;
  acceptance_rate: number | null;
}

export interface AIAccuracyMetrics {
  client_id: string;
  days: number;
  overall_acceptance_rate: number | null;
  total_decisions: number;
  by_decision: { accepted: number; skipped: number; added: number };
  sessions_total: number;
  sessions_with_feedback: number;
  by_incident_type: AIAccuracyByIncidentType[];
  weekly_trend: AIAccuracyWeeklyBucket[];
  trend: 'improving' | 'worsening' | 'stable';
}

export interface MTTRWeeklyBucket {
  week: string;
  ai_assisted_avg_hours: number | null;
  manual_avg_hours: number | null;
  ai_assisted_count: number;
  manual_count: number;
}

export interface MTTRByPriority {
  priority: string;
  ai_assisted_avg_hours: number | null;
  manual_avg_hours: number | null;
  ai_assisted_count: number;
  manual_count: number;
}

export interface MTTRMetrics {
  client_id: string;
  days: number;
  ai_assisted_avg_hours: number | null;
  manual_avg_hours: number | null;
  ai_assisted_count: number;
  manual_count: number;
  total_resolved: number;
  by_priority: MTTRByPriority[];
  weekly_trend: MTTRWeeklyBucket[];
}

export interface ErrorBudgetStatus {
  client_id: string;
  threshold: number;
  current_accuracy: number | null;
  status: 'healthy' | 'warning' | 'breached' | 'no_data';
  trend: 'improving' | 'worsening' | 'stable';
  total_decisions: number;
}

export interface AIMetricsSnapshot {
  id: string;
  client_id: string;
  week_start: string;
  acceptance_rate: number | null;
  false_positive_rate: number | null;
  ai_assisted_mttr_hours: number | null;
  manual_mttr_hours: number | null;
  total_decisions: number;
  sessions_total: number;
  sessions_with_feedback: number;
  total_resolved_tickets: number;
  created_at: string;
}

export interface PlaybookTemplateStepDef {
  id: string;
  template_id: string;
  step_number: number;
  phase: string;
  action: string;
  what_to_check: string | null;
  expected_output: string | null;
  primary_technology: string | null;
  responsible: string | null;
  conditions: string | null;
  is_required: boolean;
  step_type: 'technical' | 'communication' | 'notification' | 'documentation' | 'forensic' | 'verification' | 'escalation' | null;
}

export interface PlaybookTemplateType {
  id: string;
  type: string;
  name: string;
  version: string;
  is_builtin: boolean;
  client_id: string | null;
  detection_signals: {
    title_keywords?: string[];
    mitre_tactics?: string[];
    mitre_techniques?: string[];
    categories?: string[];
  } | null;
  ai_focus: string | null;
  steps?: PlaybookTemplateStepDef[];
  steps_count?: number;
  created_at: string;
  updated_at: string;
}

// Automation Playbooks — background rule-driven automation, distinct from
// PlaybookSession/PlaybookTemplateType above (those are advisory next-steps
// for a human analyst; these execute and can drift when a downstream vendor
// API changes).
export type AutomationStepType = 'start' | 'if-condition' | 'action' | 'end';
export type AutomationVerdict = 'silent' | 'ok' | 'failed_rot';
export type PatchProposalStatus = 'proposed' | 'accepted' | 'rejected';

export interface AutomationPlaybookStepDef {
  id?: string;
  playbook_id?: string;
  step_key: string;
  type: AutomationStepType;
  condition?: Record<string, unknown> | null;
  on_true?: string | null;
  on_false?: string | null;
  on_completion?: string | null;
  target?: Record<string, unknown> | null;
}

export interface AutomationPlaybookType {
  id: string;
  name: string;
  version: string;
  description: string | null;
  is_builtin: boolean;
  is_active: boolean;
  client_id: string | null;
  steps_count: number;
  last_run_verdict: AutomationVerdict | null;
  last_run_at: string | null;
  steps?: AutomationPlaybookStepDef[];
  created_at: string;
  updated_at: string;
}

export interface AutomationPlaybookRunFailure {
  step_key: string;
  target: Record<string, unknown> | null;
  error: string;
}

export interface AutomationPlaybookRunType {
  id: string;
  playbook_id: string;
  alert_snapshot: Record<string, unknown>;
  verdict: AutomationVerdict;
  failures: AutomationPlaybookRunFailure[];
  created_at: string;
}

export interface AutomationPlaybookPatchProposalType {
  id: string;
  run_id: string;
  step_key: string;
  proposed_target: Record<string, unknown>;
  confidence: number;
  reasoning: string | null;
  status: PatchProposalStatus;
  created_at: string;
  reviewed_by_id: string | null;
  reviewed_at: string | null;
}

export interface RuleSuggestion {
  id: string;
  client_id: string;
  template_id: string;
  rule_json: {
    id: string;
    name: string;
    confidence: 'high' | 'medium' | 'low';
    conditions: Record<string, unknown>;
    actions: string[];
  };
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface RuleReviewStatus {
  id: string;
  client_id: string;
  rule_id: string;
  rule_name: string | null;
  status: 'open' | 'reviewed' | 'dismissed';
  fire_count: number;
  false_positive_count: number;
  false_positive_rate: number | null;
  last_fired_at: string | null;
  flagged_at: string;
  updated_at: string;
  reviewed_by_id: string | null;
  reviewed_at: string | null;
  reviewed_note: string | null;
}

// AI Suggestion types
export type AISuggestionType = 'investigation_step' | 'mitre_tags' | 'root_cause' | 'triage_verdict' | 'similar_tickets';
export type AISuggestionStatus = 'pending' | 'accepted' | 'rejected' | 'edited_and_accepted';

export interface AISuggestion {
  id: string;
  ticket_id: string;
  suggestion_type: AISuggestionType;
  content: any;
  agent_name: string;
  confidence_score: number | null;
  status: AISuggestionStatus;
  reviewed_by_id: string | null;
  reviewed_at: string | null;
  edited_content: any | null;
  created_at: string;
}

// HITL Review Queue
export interface HITLReviewItem {
  session: PlaybookSession;
  ticket: {
    id: string | null;
    title: string | null;
    priority: string | null;
    status: string | null;
    assignee_id: string | null;
    assignee_name: string | null;
  } | null;
}

export interface HITLReviewQueueResponse {
  items: HITLReviewItem[];
  count: number;
  total: number;
  pending_hitl: number;
}

export interface HITLReviewStats {
  client_id: string | null;
  days: number;
  total_sessions: number;
  sessions_reviewed: number;
  sessions_pending_review: number;
  review_coverage_rate: number | null;
  hitl_flagged: number;
  hitl_flagged_rate: number | null;
  verdict_distribution: { correct: number; incorrect: number; partial: number };
  verdict_correct_rate: number | null;
  verdict_incorrect_rate: number | null;
}

// Golden Set
export interface GoldenSetEntry {
  id: string;
  client_id: string;
  session_id: string;
  name: string | null;
  notes: string | null;
  snapshot_incident_type: string | null;
  baseline_summary: string | null;
  baseline_next_steps: { step: string; rationale: string }[];
  analyst_acceptance_rate: number | null;
  analyst_outcome: string | null;
  quality_score: number | null;
  curated_by_id: string | null;
  created_at: string;
  updated_at: string | null;
  last_run_at: string | null;
  last_score: number | null;
  last_passed: boolean | null;
  relevant_chunk_ids: string[];
}

export interface GoldenSetRunResult {
  id: string;
  entry_id: string;
  run_at: string;
  new_summary: string | null;
  new_next_steps: { step: string; rationale: string }[];
  summary_similarity: number | null;
  coverage_score: number | null;
  hallucination_risk_count: number | null;
  faithfulness_score: number | null;
  overall_score: number | null;
  passed: boolean | null;
  analyst_verdict: 'approved' | 'rejected' | null;
  details: any | null;
}

export interface GoldenSetReport {
  client_id: string;
  total_entries: number;
  entries_run: number;
  entries_passed: number;
  pass_rate: number | null;
  avg_score: number | null;
  avg_hallucination_risk: number | null;
  deployment_ready: boolean;
  deployment_status: 'healthy' | 'warning' | 'failing' | 'insufficient_data';
  by_type: { incident_type: string; total: number; passed: number; pass_rate: number | null; avg_score: number | null }[];
  analyst_override_rate: number | null;
  last_run_at: string | null;
  blocked_reason: string | null;
}

export interface GoldenSetCandidate {
  session_id: string;
  incident_type: string | null;
  created_at: string | null;
  ticket_id: string | null;
  quality_score: number;
  acceptance_rate: number | null;
  steps_done: number;
  steps_total: number;
  has_root_cause: boolean;
  has_mitre_tags: boolean;
  root_cause: string | null;
  mitre_tags: string[];
  quality_breakdown: {
    acceptance: number;
    steps: number;
    root_cause: number;
    mitre: number;
  };
  ai_summary_preview: string;
}

// Curator gym types

export interface CuratorNotification {
  id: string;
  message: string;
  notification_type: string;
  priority: 'high' | 'medium' | 'info';
  client_id: string;
  ticket_id: string | null;
  session_id: string | null;
  memory_id: string | null;
  golden_entry_id: string | null;
  incident_type: string | null;
  created_at: string;
  dismissed_at: string | null;
}

export interface RecallGap {
  incident_type: string;
  golden_entry_count: number;
  avg_recall: number | null;
  ticket_count: number;
}

export interface LowRecallTicket {
  ticket_id: string;
  ticket_title: string;
  incident_type: string | null;
  recall: number | null;
  created_at: string;
}

export interface MissedChunk {
  memory_id: string;
  content_preview: string;
  incident_type: string | null;
  missed_count: number;
  is_systemic: boolean;
}

export interface PipelineStage {
  name: string;
  status: 'ok' | 'warning' | 'skipped';
  duration_ms: number | null;
  details: Record<string, any>;
}

export interface TraceMissedChunk {
  id: string;
  incident_type: string | null;
  date: string | null;
  content_preview: string | null;
}

export interface PipelineTrace {
  stages: PipelineStage[];
  recall: number | null;
  precision: number | null;
  golden_similarity: number | null;
  retrieved_count: number;
  c_star_count: number;
  missed_chunks: TraceMissedChunk[];
  retrieved_ids: string[];
}

// MTTD
export interface MTTDWeeklyBucket {
  week: string;
  avg_hours: number | null;
  count: number;
}

export interface MTTDMetrics {
  client_id: string;
  days: number;
  overall_avg_hours: number | null;
  auto_ticketed_avg_hours: number | null;
  manual_ticketed_avg_hours: number | null;
  total_with_detection_time: number;
  total_tickets: number;
  by_priority: { priority: string; avg_hours: number | null; count: number }[];
  weekly_trend: MTTDWeeklyBucket[];
}

// Data Quality
export interface DataQualityWeeklyBucket {
  week: string;
  resolved_total: number;
  root_cause_rate: number | null;
  mitre_tags_rate: number | null;
}

export interface DataQualityMetrics {
  client_id: string;
  days: number;
  resolved_tickets: {
    total: number;
    with_root_cause: number;
    with_mitre_tags: number;
    with_both: number;
    root_cause_rate: number | null;
    mitre_tags_rate: number | null;
    fully_complete_rate: number | null;
    incomplete_count: number;
  };
  by_priority: { priority: string; total: number; root_cause_rate: number | null; mitre_tags_rate: number | null; fully_complete_rate: number | null }[];
  weekly_trend: DataQualityWeeklyBucket[];
}

// Agent Uptime
export interface AgentUptimeStats {
  status: 'healthy' | 'degraded' | 'down' | 'no_data';
  uptime_1h_pct: number | null;
  uptime_24h_pct: number | null;
  total_calls_1h: number;
  total_calls_24h: number;
  successful_1h: number;
  successful_24h: number;
  avg_duration_s: number | null;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_error: string | null;
  llm_url: string;
  llm_model: string;
}

// Baseline Comparison
export interface BaselineComparison {
  client_id: string;
  has_baseline: boolean;
  baseline_meta: {
    period_start: string;
    period_end: string;
    declared_by: string;
    declared_at: string;
  } | null;
  before: {
    period: string;
    avg_mttr_hours: number | null;
    avg_mttd_hours: number | null;
    root_cause_rate: number | null;
    mitre_tags_rate: number | null;
  } | null;
  current: {
    period: string;
    avg_mttr_hours: number | null;
    avg_mttd_hours: number | null;
    root_cause_rate: number | null;
    mitre_tags_rate: number | null;
  } | null;
  deltas: {
    mttr_hours_delta: number | null;
    mttr_pct_change: number | null;
    mttd_hours_delta: number | null;
    mttd_pct_change: number | null;
  };
}

// Template Step Suggestions
export interface TemplateStepSuggestion {
  id: string;
  client_id: string;
  template_id: string | null;
  incident_type: string;
  suggestion_type: 'add' | 'remove';
  template_step_id: string | null;
  step_data: {
    action: string;
    phase: string;
    what_to_check: string;
    expected_output: string;
    primary_technology: string;
    responsible: string;
    is_required: boolean;
    step_type: string;
  } | null;
  reason: string | null;
  evidence: {
    // 'remove' evidence
    skip_count?: number;
    done_count?: number;
    total?: number;
    skip_rate?: number;
    // 'add' evidence
    add_count?: number;
    session_count?: number;
    action?: string;
    phase?: string;
  } | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by_id: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TemplateStepSuggestionsResponse {
  suggestions: TemplateStepSuggestion[];
  count: number;
}

// Auto-Tune
export interface AutoTuneResult {
  client_id: string;
  actions_analysed: number;
  suggestions_created: number;
  suggestions_already_pending: number;
  thresholds: { min_samples: number; min_skip_rate: number };
}

// Error Budget Acknowledgment
export interface ErrorBudgetAcknowledgment {
  threshold: number;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  acknowledged_note: string | null;
}

// Root state interface
export interface RootState {
  auth: AuthState;
  tickets: TicketsState;
  clients: ClientsState;
  decodingAgent: DecodingAgentState;
  users: UsersState;
  alerts: AlertsState;
  correlations: CorrelationsState;
  iocs: IOCsState;
  playbooks: PlaybooksState;
  integrationData: import('./integration').IntegrationDataState;
  notifications: NotificationsState;
}

